/**
 * Register the BuildIntel service worker on every page load (not just when
 * the tradie opens Push Settings) so that a version bump in `public/sw.js`
 * proactively purges browser caches and reloads any stale tabs.
 *
 * The SW's `activate` handler:
 *   1. Deletes every named cache (defensive — clears legacy pre-cached shells)
 *   2. Claims all clients so the new worker controls this tab immediately
 *   3. Posts `{type: "buildintel:sw-updated"}` to every window client
 *
 * This module wires the client-side half: on the FIRST such message received
 * per session, we hard-reload once. Subsequent messages are ignored (a
 * single reload is enough — no reload-loop risk).
 *
 * Only runs when a Service Worker API is available. Historically we gated
 * this on `NODE_ENV === "production"` to avoid double-caching during hot
 * reload, but the Emergent preview serves the CRA dev server directly to
 * tradies (there's no separate `yarn build` stage), so gating on that
 * env var meant the SW never registered in the wild — and cache purges
 * never ran. Guard removed on 2026-02-15 after a "still-pale-picker"
 * report traced to precisely this. `caches.delete` is a no-op when no
 * caches exist, so re-registering during hot reload is harmless.
 */
export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .catch(() => {
        // Register failures are non-fatal — push + auto-reload just won't
        // work on this device. Silence to avoid noisy dev consoles.
      });
  });

  let reloaded = false;
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (reloaded) return;
    const msg = event.data;
    if (msg && msg.type === "buildintel:sw-updated") {
      reloaded = true;
      // iOS SAFARI FIX: never reload while a blob: download / PDF preview is
      // in flight — an auto-reload mid-download triggers "WebKitBlobResource
      // error 1" and clobbers the tradie's PDF. `downloadFile` /
      // `downloadPdf` set `window.__biDownloadInProgress` for 60s.
      const IN_FLIGHT_GRACE_MS = 65_000;
      const now = Date.now();
      const dlAt = (typeof window !== "undefined" && window.__biDownloadInProgress) || 0;
      const wait = dlAt && (now - dlAt) < IN_FLIGHT_GRACE_MS
        ? IN_FLIGHT_GRACE_MS - (now - dlAt) + 500
        : 200;
      setTimeout(() => window.location.reload(), wait);
    }
  });
}
